import { useGetStudentsSchedulingDetails, type GetStudentsSchedulingDetailsResponseData } from '@/features/scheduling/api/getStudentsSchedulingDetails';
import type { ApiErrorResponse } from '@/lib/apiClient';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { Appointment } from '@/types/scheduling';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

export type CancelStep = 'cancel' | 'success' | 'lateCancelError' | 'error';

export interface CancelFlowData {
  config: {
    isLoading: boolean;
    error: ApiErrorResponse | null;
    data?: GetStudentsSchedulingDetailsResponseData;
  };
  appointment?: Appointment;
  applyToFollowingSessions: boolean;
}
export interface CancelFlowContextType {
  currentStep: CancelStep;
  data: CancelFlowData;
  goToStep: (step: CancelStep) => void;
  isCancelDialogOpen: boolean;
  openCancelDialog: (appointment: Appointment, openButtonRef?: React.RefObject<HTMLButtonElement | null>) => void;
  closeCancelDialog: () => void;
  resetFlow: () => void;
  updateData: <K extends keyof CancelFlowData>(key: K, value: CancelFlowData[K]) => void;
}

const CancelFlowContext = createContext<CancelFlowContextType | undefined>(undefined);

const getInitialData = (
  isLoading: boolean,
  error: ApiErrorResponse | null,
  data?: GetStudentsSchedulingDetailsResponseData,
  appointment?: Appointment,
): CancelFlowData => ({
  appointment,
  applyToFollowingSessions: false,
  config: {
    isLoading,
    error,
    data,
  },
});

interface CancelFlowProviderProps {
  children: React.ReactNode;
}

export const CancelFlowProvider: React.FC<CancelFlowProviderProps> = ({ children }) => {
  const [currentStep, setCurrentStep] = React.useState<CancelStep>('cancel');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const schedulingDetailsQuery = useGetStudentsSchedulingDetails({});

  const [data, setData] = React.useState<CancelFlowData>(() =>
    getInitialData(schedulingDetailsQuery.isLoading, schedulingDetailsQuery.error, schedulingDetailsQuery.data),
  );

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      config: {
        isLoading: schedulingDetailsQuery.isLoading,
        error: schedulingDetailsQuery.error,
        data: schedulingDetailsQuery.data,
      },
    }));
  }, [schedulingDetailsQuery.isLoading, schedulingDetailsQuery.error, schedulingDetailsQuery.data]);

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const openButtonRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    const ref = timeoutRef;
    return () => {
      if (ref.current) {
        clearTimeout(ref.current);
      }
    };
  }, []);

  const openCancelDialog = useCallback((appointmentToCancel: Appointment, buttonRef?: React.RefObject<HTMLButtonElement | null>) => {
    // Store the button ref that opened this dialog
    openButtonRef.current = buttonRef?.current ?? null;

    setData((prev) => ({ ...prev, appointment: appointmentToCancel }));
    setIsCancelDialogOpen(true);
  }, []);

  const updateData = React.useCallback(<K extends keyof CancelFlowData>(key: K, value: CancelFlowData[K]) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const resetFlow = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCurrentStep('cancel');
      setData((prev) => ({
        applyToFollowingSessions: false,
        appointment: undefined,
        config: prev.config,
      }));
    }, 300);
  }, []);

  const closeCancelDialog = useCallback(() => {
    void captureAnalyticsEvent('cancelling_closed', { appointment_id: data.appointment?.appointment_id });
    setIsCancelDialogOpen(false);
    resetFlow();

    if (openButtonRef.current) {
      setTimeout(() => {
        openButtonRef.current?.focus();
      }, 0);
    }
  }, [resetFlow, data.appointment?.appointment_id]);

  const goToStep = React.useCallback((step: CancelStep) => {
    setCurrentStep(step);
  }, []);

  const contextValue = useMemo(
    () => ({
      isCancelDialogOpen,
      openCancelDialog,
      closeCancelDialog,
      currentStep,
      goToStep,
      resetFlow,
      data,
      updateData,
    }),
    [isCancelDialogOpen, openCancelDialog, closeCancelDialog, data, currentStep, goToStep, resetFlow, updateData],
  );

  return <CancelFlowContext value={contextValue}>{children}</CancelFlowContext>;
};

export { CancelFlowContext };
